import { AuthProvider, useAuth } from "@/lib/authContext";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { Sidebar } from "@/components/admin/Sidebar";
import { Header } from "@/components/admin/Header";
import { SessionExpiryWatcher } from "@/components/admin/SessionExpiryWatcher";
import { ToastProvider, Spinner } from "@/components/ui";

function AdminLayoutContent() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !user) {
      // Already on /login? Stay put — the URL would be /login?from=...
      if (location.pathname.startsWith("/login")) return;
      const next = `/login?from=${encodeURIComponent(location.pathname + location.search)}`;
      navigate(next, { replace: true });
    }
  }, [user, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="!h-8 !w-8" />
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--color-tertiary-label)]">
            Loading workspace
          </p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <ToastProvider>
      <SessionExpiryWatcher />
      <div className="admin-shell">
        <Sidebar />
        <div className="flex min-h-screen flex-col">
          <Header />
          <main className="admin-content">
            <PageEnter>
              <Outlet />
            </PageEnter>
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}

/**
 * Page-enter wrapper that re-keys on `pathname` so the `apple-page-enter`
 * keyframe re-fires on every route change.
 */
function PageEnter({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <div key={location.pathname} className="apple-page-enter">
      {children}
    </div>
  );
}

export function AdminLayout() {
  return (
    <AuthProvider>
      <AdminLayoutContent />
    </AuthProvider>
  );
}